/**
 * Maps an audit `entity_type` (the DB table name) to the admin page that
 * displays the record, so an audit row can deep-link to the record itself.
 * Entities without a record page (bay_schedule, permission, import rows, …)
 * are absent: the audit trail then offers an in-place ghost snapshot instead.
 */
const ROUTE_BY_TABLE: Record<string, string> = {
  customer: "/admin/customers",
  haulier: "/admin/hauliers",
  cse: "/admin/cse",
  site: "/admin/sites",
  bay: "/admin/bays",
  contact: "/admin/contacts",
  user: "/admin/users",
  group: "/admin/groups",
  load: "/admin/loads",
  load_collection: "/admin/loads",
  load_invoice: "/admin/loads",
  rate_card: "/admin/rate-cards",
  vehicle_type: "/admin/vehicle-types",
  notification: "/admin/notifications",
  language: "/admin/languages",
  translation_key: "/admin/translations",
  translation_value: "/admin/translations",
  app_setting: "/admin/settings",
};

/** The slice of an audit entry needed to build a deep link. */
export interface AuditLinkSource {
  entity_type: string;
  entity_id: string | null;
  action: string;
  changes: { field_name: string }[];
}

/**
 * Deep link that carries the audit context to the record page:
 * `r` the record id, `a` the action, `f` the changed field names and
 * `ar` the audit entry id. The target page uses these to highlight the
 * changed fields with a thick yellow border, and to render a ghost copy
 * of the record when it was deleted (see `lib/auditHighlight.ts`).
 */
export function auditRouteFor(entry: AuditLinkSource, auditId?: number | null): string | null {
  const base = ROUTE_BY_TABLE[entry.entity_type];
  if (!base) return null;
  const params = new URLSearchParams();
  // `e` tells pages that serve several entity types (loads, sites,
  // translations, …) which table the audit entry points at.
  if (entry.entity_type) params.set("e", entry.entity_type);
  if (entry.entity_id) params.set("r", entry.entity_id);
  if (entry.action) params.set("a", entry.action);
  const fields = [...new Set(entry.changes.map((c) => c.field_name))].slice(0, 20);
  if (fields.length > 0) params.set("f", fields.join(","));
  if (auditId != null) params.set("ar", String(auditId));
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}
